import type { AudioFrame } from "@dodobop/audio";
import type { CameraMoveId, PostChainSettings } from "@dodobop/engine";
import type { OverlayState } from "@dodobop/overlay";
import type { ParamValues } from "@dodobop/params";

/**
 * A second window showing the visuals alone, for a television or a projector.
 *
 * The room should see the work and nobody should see the controls. Fullscreen on one display
 * cannot do that, because the controls and the visuals are the same window. So a second window
 * opens with nothing in it but the canvas, gets dragged to the other screen, and is driven from
 * the first: the same scene, the same tuning, and the same analysed audio, frame for frame.
 *
 * Everything travels over a BroadcastChannel, which never leaves the browser. The display
 * window does no listening of its own; it could not, since a microphone can only be opened
 * once and the analysis has to agree exactly or the two screens would drift apart.
 */

export const DISPLAY_QUERY = "display";

const CHANNEL_NAME = "dodobop-screen";

/** Frames are published at this rate rather than all sixty; the difference is not visible. */
const FRAME_HZ = 30;

export interface DisplayScene {
  readonly sceneId: string;
  readonly params: ParamValues;
  readonly post: PostChainSettings;
  readonly overlay: OverlayState;
  readonly cameraMove: CameraMoveId;
  /** Words the field gathers into, rebuilt on the display rather than sent as geometry. */
  readonly message: string;
}

export interface DisplayMask {
  readonly width: number;
  readonly height: number;
  /** One byte per pixel: covered or not. */
  readonly data: Uint8Array;
}

type Envelope =
  | { kind: "scene"; scene: DisplayScene }
  | { kind: "frame"; frame: AudioFrame }
  | { kind: "mask"; mask: DisplayMask | null }
  | { kind: "hello" }
  | { kind: "goodbye" };

export function isDisplayWindow(): boolean {
  const search = new URLSearchParams(window.location.search);
  return search.has(DISPLAY_QUERY) || search.has("stage");
}

export function screenLinkSupported(): boolean {
  return typeof BroadcastChannel === "function";
}

export class ScreenLink {
  private channel: BroadcastChannel | null = null;
  private handle: Window | null = null;
  private lastScene: DisplayScene | null = null;
  private lastFramePost = 0;

  get open(): boolean {
    return this.handle !== null && !this.handle.closed;
  }

  /**
   * Opens the display window. Must be called from a real click or the browser refuses.
   *
   * Returns false when the popup was blocked, which is worth telling the user about because
   * nothing else will: a blocked popup is silent.
   */
  present(): boolean {
    if (!screenLinkSupported()) return false;
    this.ensureChannel();
    if (this.open) {
      this.handle?.focus();
      return true;
    }
    const url = new URL(window.location.href);
    url.searchParams.set(DISPLAY_QUERY, "1");
    this.handle = window.open(url.toString(), "dodobop-display", "width=1280,height=720");
    return this.handle !== null;
  }

  dismiss(): void {
    this.post({ kind: "goodbye" });
    this.handle?.close();
    this.handle = null;
  }

  publishScene(scene: DisplayScene): void {
    this.lastScene = scene;
    this.post({ kind: "scene", scene });
  }

  publishFrame(frame: AudioFrame): void {
    const now = performance.now();
    if (now - this.lastFramePost < 1000 / FRAME_HZ) return;
    this.lastFramePost = now;
    this.post({ kind: "frame", frame });
  }

  publishMask(mask: DisplayMask | null): void {
    this.post({ kind: "mask", mask });
  }

  /** The display side. Asks for the current scene, since it may open mid-set. */
  listen(handlers: {
    onScene: (scene: DisplayScene) => void;
    onFrame: (frame: AudioFrame) => void;
    onMask: (mask: DisplayMask | null) => void;
    onGoodbye: () => void;
  }): () => void {
    if (!screenLinkSupported()) return () => undefined;
    this.ensureChannel();
    const channel = this.channel!;
    const onMessage = (event: MessageEvent<Envelope>): void => {
      const message = event.data;
      if (message.kind === "scene") handlers.onScene(message.scene);
      else if (message.kind === "frame") handlers.onFrame(message.frame);
      else if (message.kind === "mask") handlers.onMask(message.mask);
      else if (message.kind === "goodbye") handlers.onGoodbye();
    };
    channel.addEventListener("message", onMessage);
    this.post({ kind: "hello" });
    return () => channel.removeEventListener("message", onMessage);
  }

  dispose(): void {
    this.channel?.close();
    this.channel = null;
  }

  private ensureChannel(): void {
    if (this.channel !== null || !screenLinkSupported()) return;
    this.channel = new BroadcastChannel(CHANNEL_NAME);
    // A display that opens mid-set has to be told what is already playing.
    this.channel.addEventListener("message", (event: MessageEvent<Envelope>) => {
      if (event.data.kind === "hello" && this.lastScene !== null) {
        this.post({ kind: "scene", scene: this.lastScene });
      }
    });
  }

  private post(message: Envelope): void {
    this.ensureChannel();
    this.channel?.postMessage(message);
  }
}
